import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import CaseAccordion from '../../CaseAccordion';
import NoAccess from '../../NoAccess';
import HardwareList from '../../HardwareList';
import CalculationInput from '../../step3/CalculationInput';

import Button from '../../../../../shared/Button';
import Input from '../../../../../shared/Input';
import Dropzone from '../../../../../shared/dropzone/Dropzone';

import { updateCase } from '../../../../../../actions/casesActions';
import { caseConstants } from '../../../../../../constants';

const { caseNotifications, fileTypes, statuses } = caseConstants;

const OsteotomyStep10 = ({ classes, onClose, activeCase, updateCase, products, userId }) => {
  const hardware = activeCase.hardware ? activeCase.hardware.map((item) => ({ ...item, id: Math.random() })) : [];

  const [hardwareList, setHardwareList] = useState(hardware ? hardware : []);
  const [notes, setNotes] = useState('');
  const [sheets, setSheets] = useState([]);
  const [xRays, setXRays] = useState([]);
  const [aoas, setAoas] = useState([]);
  const [allografts, setAllografts] = useState([]);

  const [preOp, setPreOp] = useState(0);
  const [postOp, setPostOp] = useState(0);
  const [wedgeHeight, setWedgeHeight] = useState(0);

  const handleAddHardware = (product) => {
    const list = [...hardwareList];
    const hardware = list.find((item) => item.product === product.product && item.category === product.category);

    if (hardware) {
      hardware.amount += Number(product.amount);
      setHardwareList(list);
    } else {
      setHardwareList([...hardwareList, product]);
    }
  };

  const handleRemoveHardware = (id) => {
    setHardwareList(hardwareList.filter((item) => item.id !== id));
  };

  const handleAmountChange = (id, amount) => {
    const list = [...hardwareList];
    const hardware = list.find((item) => item.id === id);

    hardware.amount = amount;
    setHardwareList(list);
  };

  const handleCategoryChange = (id, category) => {
    const list = [...hardwareList];
    const hardware = list.find((item) => item.id === id);

    hardware.category = category;
    setHardwareList(list);
  };

  const handleChange = (id, product) => {
    const list = [...hardwareList];
    const hardware = list.find((item) => item.id === id);

    hardware.product = product;
    setHardwareList(list);
  };

  const handleSheetsUpload = (files) => {
    setSheets([...sheets, ...files.filter((file) => !sheets.map((sheet) => sheet.name).includes(file.name))]);
  };
  const handleXRaysUpload = (files) => {
    setXRays([...xRays, ...files.filter((file) => !xRays.map((xRay) => xRay.name).includes(file.name))]);
  };
  const handleAoasUpload = (files) => {
    setAoas([...aoas, ...files.filter((file) => !aoas.map((aoa) => aoa.name).includes(file.name))]);
  };
  const handleAllograftsUpload = (files) => {
    setAllografts([
      ...allografts,
      ...files.filter((file) => !allografts.map((allograft) => allograft.name).includes(file.name)),
    ]);
  };

  const handleRemoveSheets = (name) => {
    setSheets(sheets.filter((file) => file.name !== name));
  };
  const handleRemoveXRays = (name) => {
    setXRays(xRays.filter((file) => file.name !== name));
  };
  const handleRemoveAoas = (name) => {
    setAoas(aoas.filter((file) => file.name !== name));
  };
  const handleRemoveAllografts = (name) => {
    setAllografts(allografts.filter((file) => file.name !== name));
  };

  const handleSubmit = () => {
    const now = moment().format();
    const files = [...activeCase.files];
    const newFiles = [];

    sheets.forEach((file) => {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.chargedSheet}_${file.name}`,
        name: file.name,
        type: fileTypes.chargedSheet,
        date: now,
        userId,
      });
      newFiles.push(file);
    });
    xRays.forEach((file) => {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.xRay}_${file.name}`,
        name: file.name,
        type: fileTypes.xRay,
        date: now,
        userId,
      });
      newFiles.push(file);
    });
    aoas.forEach((file) => {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.aoa}_${file.name}`,
        name: file.name,
        type: fileTypes.aoa,
        date: now,
        userId,
      });
      newFiles.push(file);
    });
    allografts.forEach((file) => {
      files.unshift({
        id: `${activeCase.id}_${fileTypes.allograft}_${file.name}`,
        name: file.name,
        type: fileTypes.allograft,
        date: now,
        userId,
      });
      newFiles.push(file);
    });

    const updatedCase = {
      ...activeCase,
      step: 11,
      activity: [{ ...caseNotifications[10].success, date: now, userId }, ...activeCase.activity],
      files,
      completionDates: { ...activeCase.completionDates, step10: now },
      completedBy: { ...activeCase.completedBy, step10: userId },
      hardware: hardwareList.map((item) => ({
        product: item.product,
        amount: item.amount,
        category: item.category,
      })),
      notes: { ...activeCase.notes, step10: notes },
      status: statuses.inProgress,
    };

    if (activeCase.skipPSI) {
      updatedCase.surgicalInfo = {
        preOpAlignment: preOp,
        postOpAlignment: postOp,
        wedgeHeight,
      };
    }

    updateCase(updatedCase, newFiles).then(() => onClose());
  };

  const isDisabled = () => activeCase.skipPSI && !preOp && !postOp && wedgeHeight;

  return (
    <div className="case-step-container">
      <div className="case-step-content">
        <div className="case-step-row">
          <div className="column">
            <div className="modal-item-circle">10</div>
          </div>
          <div className="modal-item-content">
            <div>Usage Report</div>
            {activeCase.access ? (
              <Fragment>
                {activeCase.skipPSI && (
                  <div className="modal-item-block p-t-md">
                    <div className="block-header">Surgical information</div>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <CalculationInput
                          value={preOp}
                          onChange={setPreOp}
                          label="Pre_Operative_Alignment"
                          unit="degrees"
                          max={180}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <CalculationInput
                          value={postOp}
                          onChange={setPostOp}
                          label="Post_Operative_Alignment"
                          unit="degrees"
                          max={180}
                        />
                        <div className="m-t-lg">
                          <CalculationInput
                            value={wedgeHeight}
                            onChange={setWedgeHeight}
                            label="Wedge_Height"
                            unit="mm"
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                )}
                <HardwareList
                  classes={classes}
                  hardwareList={hardwareList}
                  products={products}
                  onAdd={handleAddHardware}
                  onRemove={handleRemoveHardware}
                  onAmountChange={handleAmountChange}
                  onCategoryChange={handleCategoryChange}
                  onChange={handleChange}
                  title="Confirmed Hardware"
                  procedureFlow={activeCase.procedureFlow}
                />
                <div className="m-t-md">
                  <Input
                    id="notes"
                    type="text"
                    fullWidth
                    multiline
                    placeholder="Usage notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                <div className="m-t-lg">
                  <div className="modal-item-block p-t-md">
                    <div className="block-header">Upload Charge Sheet</div>
                    <Dropzone
                      files={sheets}
                      onUpload={handleSheetsUpload}
                      icon="image"
                      onRemove={handleRemoveSheets}
                    />
                  </div>
                  <div className="modal-item-block">
                    <div className="block-header">Attach X-ray(s)</div>
                    <Dropzone files={xRays} onUpload={handleXRaysUpload} icon="x-ray" onRemove={handleRemoveXRays} />
                  </div>
                  <div className="modal-item-block">
                    <div className="block-header">AOA registry (green form)</div>
                    <Dropzone files={aoas} onUpload={handleAoasUpload} icon="aoa" onRemove={handleRemoveAoas} />
                  </div>
                  <div className="modal-item-block">
                    <div className="block-header">Allograft Tracking Form</div>
                    <Dropzone
                      files={allografts}
                      onUpload={handleAllograftsUpload}
                      icon="graft"
                      onRemove={handleRemoveAllografts}
                    />
                  </div>
                </div>
              </Fragment>
            ) : (
              <NoAccess />
            )}
          </div>
        </div>
        <CaseAccordion currentStep={10} />
      </div>
      <div className="case-modal-actions">
        <div className="secondary pointer" onClick={onClose}>
          Close
        </div>
        <Button text="Submit" color="primary" onClick={handleSubmit} disabled={!activeCase.access || isDisabled()} />
      </div>
    </div>
  );
};

OsteotomyStep10.propTypes = {
  activeCase: PropTypes.object,
  products: PropTypes.array,
  onClose: PropTypes.func,
  updateCase: PropTypes.func,
};

const mapStateToProps = (state) => {
  const procedure = state.procedures.list.find((procedure) => procedure.id === state.cases.activeCase.procedure);
  const products = procedure ? procedure.products : [];

  return {
    activeCase: state.cases.activeCase,
    userId: state.user.currentUser.uid,
    products,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateCase: (newCase, newFiles) => dispatch(updateCase(newCase, 10, newFiles)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OsteotomyStep10);
